import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  HairlineCard,
  Icons,
  iconSize,
  List,
  ListItem,
  ListItemText,
  palette,
  Stack,
  StatusChip,
  Tab,
  Tabs,
  Typography,
} from '@munifocus/ui';
import type { AssistantStatus, CaseSummary, EscalationRisk } from '@munifocus/contracts-gov';
import {
  useAssistantStatus,
  useCaseFacts,
  useEscalationRisk,
  useGenerateSummary,
} from '../../lib/ai-assistant-api';
import { useSessionAccess } from '../../lib/administration-api';
import { useAuth } from '../../providers/AuthProvider';
import { apiErrorMessage } from '../../lib/mutation-toast';
import { localizeStatuteRef, stageLabelKey } from '../../lib/file-display';
import { currentLocale, formatDay, RISK_STATUS } from '../../lib/assistant-display';
import { AiLabel } from './AiLabel';
import { AssistantDraft } from './AssistantDraft';
import { AssistantPhotos } from './AssistantPhotos';

/** Why the AI half is unavailable, in the officer's words. */
function UnavailableNote({ status }: { status: AssistantStatus }) {
  const { t } = useTranslation();
  if (status.aiAvailable || !status.reason) return null;
  return (
    <Alert severity="info" sx={{ mb: 2 }}>
      {t(`gov.assistant.unavailable.${status.reason}`)}
    </Alert>
  );
}

export function RiskSummary({ risk }: { risk: EscalationRisk }) {
  const { t } = useTranslation();
  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2">{t('gov.assistant.risk.title')}</Typography>
        <StatusChip
          status={RISK_STATUS[risk.level]}
          label={t(`gov.assistant.risk.level.${risk.level}`)}
        />
        <Typography variant="caption" sx={{ color: palette.text.secondary }}>
          {t('gov.assistant.risk.score', { score: risk.score })}
        </Typography>
      </Stack>
      {risk.reasons.length === 0 ? (
        <Typography variant="body2" sx={{ color: palette.text.secondary }}>
          {t('gov.assistant.risk.noReasons')}
        </Typography>
      ) : (
        <List dense disablePadding>
          {risk.reasons.map((r) => (
            <ListItem key={r.code} disableGutters>
              <ListItemText
                primary={t(`gov.assistant.risk.reason.${r.code}`, r.params ?? {})}
                secondary={t('gov.assistant.risk.points', { count: r.weight })}
              />
            </ListItem>
          ))}
        </List>
      )}
      <Typography variant="caption" sx={{ color: palette.text.secondary }}>
        {t('gov.assistant.risk.ruleBased')}
      </Typography>
    </Box>
  );
}

function SummaryFacts({ summary }: { summary: CaseSummary }) {
  const { t, i18n } = useTranslation();
  const { facts, nextStep, deadlines, timeline } = summary;
  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="subtitle2">{t('gov.assistant.summary.nextStep')}</Typography>
        <Typography variant="body2">
          {t(`gov.assistant.nextStep.${nextStep.code}`)}
          {nextStep.statuteRef ? ` · ${localizeStatuteRef(nextStep.statuteRef)}` : ''}
        </Typography>
        {nextStep.dueOn && (
          <Typography variant="body2" sx={{ color: palette.text.secondary }}>
            {t('gov.assistant.summary.dueOn', { date: formatDay(nextStep.dueOn, i18n.language) })}
          </Typography>
        )}
      </Box>
      <Typography variant="body2" sx={{ color: palette.text.secondary }}>
        {t('gov.assistant.summary.facts', {
          stage: t(stageLabelKey(facts.stage)),
          days: facts.daysOpen,
        })}
        {facts.daysInStage !== null
          ? ` ${t('gov.assistant.summary.inStage', { count: facts.daysInStage })}`
          : ''}
      </Typography>
      <Box>
        <Typography variant="subtitle2">{t('gov.assistant.summary.deadlines')}</Typography>
        {deadlines.length === 0 ? (
          <Typography variant="body2" sx={{ color: palette.text.secondary }}>
            {t('gov.assistant.summary.noDeadlines')}
          </Typography>
        ) : (
          <List dense disablePadding>
            {deadlines.map((d) => (
              <ListItem key={`${d.kind}-${d.deadlineIso}`} disableGutters>
                <ListItemText
                  primary={`${t(`gov.dashboard.deadlines.kind.${d.kind}`)} · ${localizeStatuteRef(d.statuteRef)}`}
                  secondary={
                    d.expired
                      ? t('gov.assistant.summary.expired', {
                          date: formatDay(d.deadlineIso, i18n.language),
                        })
                      : t('gov.assistant.summary.ends', {
                          date: formatDay(d.deadlineIso, i18n.language),
                          count: d.daysRemaining,
                        })
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </Box>
      {timeline.length > 0 && (
        <Box>
          <Typography variant="subtitle2">{t('gov.assistant.summary.timeline')}</Typography>
          <List dense disablePadding>
            {timeline.map((e) => (
              <ListItem key={`${e.at}-${e.action}`} disableGutters>
                <ListItemText
                  primary={t(`gov.activity.actions.${e.action.replace(/\./g, '_')}`, {
                    stage: e.to ? t(`gov.stages.${e.to}`) : '',
                    defaultValue: t('gov.activity.unknownAction'),
                  })}
                  secondary={formatDay(e.at, i18n.language)}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Stack>
  );
}

function SummaryTab({ fileId, status }: { fileId: string; status: AssistantStatus }) {
  const { t, i18n } = useTranslation();
  const facts = useCaseFacts(fileId);
  const generate = useGenerateSummary(fileId);
  const summary = generate.data ?? facts.data;
  const narrative = generate.data?.narrative ?? null;

  if (facts.isLoading) return <CircularProgress size={24} />;
  if (facts.isError || !summary) {
    return <Alert severity="error">{t('gov.assistant.loadError')}</Alert>;
  }
  return (
    <Stack spacing={2}>
      <SummaryFacts summary={summary} />
      <Divider />
      {status.aiAvailable && (
        <Box>
          {narrative ? (
            <>
              <AiLabel provenance={narrative} />
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {narrative.text}
              </Typography>
            </>
          ) : (
            generate.data?.narrativeUnavailable === 'failed' && (
              <Alert severity="warning">{t('gov.assistant.summary.narrativeFailed')}</Alert>
            )
          )}
          {generate.isError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {apiErrorMessage(generate.error, t('gov.assistant.summary.narrativeFailed'))}
            </Alert>
          )}
          <Button
            sx={{ mt: 1.5 }}
            variant="outlined"
            startIcon={<Icons.Sparkles size={iconSize.md} aria-hidden />}
            disabled={generate.isPending}
            onClick={() => generate.mutate({ locale: currentLocale(i18n.language) })}
          >
            {generate.isPending
              ? t('gov.assistant.working')
              : narrative
                ? t('gov.assistant.summary.regenerate')
                : t('gov.assistant.summary.generate')}
          </Button>
        </Box>
      )}
    </Stack>
  );
}

type TabKey = 'summary' | 'risk' | 'draft' | 'photos';

/**
 * The AI case assistant on file detail.
 *
 * Shown only to someone holding `ai:assist` on this file, and only where the
 * municipality turned the assistant on. Facts and the risk indicator are
 * always there once it is on; the draft and photo tabs appear only when a
 * model may be used for this file. Nothing here is ever sent anywhere — a
 * draft is text for the officer to copy into their own correspondence.
 */
export function CaseAssistantPanel({ fileId }: { fileId: string }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  // The session query runs only once it knows whose session it is; without the
  // id it never ran, and the panel never appeared for anyone.
  const session = useSessionAccess('file', fileId, user?.id);
  const permissions = session.data?.permissions ?? [];
  const allowed = permissions.includes('ai:assist');
  const status = useAssistantStatus(fileId, allowed);
  const risk = useEscalationRisk(fileId, allowed && !!status.data?.enabled);
  const [tab, setTab] = useState<TabKey>('summary');

  if (!allowed || !status.data?.enabled) return null;
  const s = status.data;
  const canDraft = s.aiAvailable && permissions.includes('document:draft');

  return (
    <HairlineCard sx={{ p: 2, mt: 2 }} data-testid="case-assistant">
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 0.5 }}>
        <Icons.Sparkles size={iconSize.md} aria-hidden color={palette.brand.accent} />
        <Typography variant="h6" component="h2">
          {t('gov.assistant.title')}
        </Typography>
      </Stack>
      <Typography variant="body2" sx={{ color: palette.text.secondary, mb: 2 }}>
        {t('gov.assistant.subtitle')}
      </Typography>
      <UnavailableNote status={s} />
      <Tabs
        value={tab}
        onChange={(_e, value: TabKey) => setTab(value)}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ mb: 2 }}
      >
        <Tab value="summary" label={t('gov.assistant.tabs.summary')} />
        <Tab value="risk" label={t('gov.assistant.tabs.risk')} />
        {canDraft && <Tab value="draft" label={t('gov.assistant.tabs.draft')} />}
        {s.aiAvailable && <Tab value="photos" label={t('gov.assistant.tabs.photos')} />}
      </Tabs>
      {tab === 'summary' && <SummaryTab fileId={fileId} status={s} />}
      {tab === 'risk' &&
        (risk.data ? (
          <RiskSummary risk={risk.data} />
        ) : risk.isError ? (
          <Alert severity="error">{t('gov.assistant.loadError')}</Alert>
        ) : (
          <CircularProgress size={24} />
        ))}
      {tab === 'draft' && canDraft && <AssistantDraft fileId={fileId} />}
      {tab === 'photos' && s.aiAvailable && <AssistantPhotos fileId={fileId} />}
    </HairlineCard>
  );
}
